import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface DialogProps {
  open: boolean;
  onClose: () => void;
  titleId: string;
  title: string;
  children: ReactNode;
  variant?: 'dialog' | 'sheet';
}

/**
 * Barrierefreier Dialog auf Basis von `<dialog>` (Fokusfalle, Escape schließt, A11Y-004).
 * Auf Mobile per `variant="sheet"` als Bottom Sheet gestylt (FR-TEST-003).
 */
export function Dialog({
  open,
  onClose,
  titleId,
  title,
  children,
  variant = 'dialog',
}: DialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const node = dialogRef.current;
    if (!node) return;

    if (open && !node.open) {
      previouslyFocused.current = document.activeElement as HTMLElement | null;
      node.showModal();
    } else if (!open && node.open) {
      node.close();
    }
  }, [open]);

  useEffect(() => {
    const node = dialogRef.current;
    if (!node) return;

    function handleClose(): void {
      onClose();
      previouslyFocused.current?.focus();
    }

    node.addEventListener('close', handleClose);
    return () => node.removeEventListener('close', handleClose);
  }, [onClose]);

  function handleBackdropClick(event: React.MouseEvent<HTMLDialogElement>): void {
    if (event.target === dialogRef.current) {
      dialogRef.current?.close();
    }
  }

  return createPortal(
    <dialog
      ref={dialogRef}
      className={`app-dialog app-dialog--${variant} no-print`}
      aria-labelledby={titleId}
      onClick={handleBackdropClick}
      onCancel={(event) => {
        event.preventDefault();
        dialogRef.current?.close();
      }}
    >
      <div className="app-dialog__content">
        <div className="app-dialog__header">
          <h2 id={titleId} className="app-dialog__title">
            {title}
          </h2>
          <button
            type="button"
            className="app-dialog__close"
            onClick={() => dialogRef.current?.close()}
            aria-label="Schließen"
          >
            ×
          </button>
        </div>
        <div className="app-dialog__body">{children}</div>
      </div>
    </dialog>,
    document.body,
  );
}
